export function initBeverageCatalogue(root) {
  if (root.dataset.bvCatalogueReady) return () => {};
  root.dataset.bvCatalogueReady = 'true';
  const doc = root.ownerDocument;
  const view = doc.defaultView;
  const header = doc.querySelector('[data-lum-header]');
  let frame;
  let catalogueReturn;
  let restoreTimer;
  let areaFrame;
  const measure = () => {
    const rect = header?.getBoundingClientRect();
    const position = header && view.getComputedStyle(header).position;
    const offset = rect && ['sticky', 'fixed'].includes(position) && rect.top <= 1 ? Math.max(0, Math.min(rect.height, rect.bottom)) : 0;
    doc.documentElement.style.setProperty('--bv-header-offset', `${offset}px`);
    doc.documentElement.style.setProperty('--bv-catalogue-height', `${root.getBoundingClientRect().height}px`);
  };
  const schedule = () => {
    view.cancelAnimationFrame(frame);
    frame = view.requestAnimationFrame(measure);
  };
  const access = root.querySelector('[data-bv-area-access]');
  const area = doc.getElementById('beverage-area-menu');
  if (area && access) access.setAttribute('href', '#beverage-area-menu');
  let categories = doc.getElementById('beverage-categories');
  const categoryAccess = root.querySelector('[data-bv-category-access]');
  const compact = view.matchMedia?.('(max-width: 1023px)');
  const resizeCategories = () => {
    categories = doc.getElementById('beverage-categories');
    if (!categories || !compact) return;
    const focused = doc.activeElement;
    const inside = categories.contains(focused);
    categories.open = !compact.matches;
    if (inside && compact.matches) categories.querySelector('summary')?.focus({ preventScroll: true });
    else if (!compact.matches && ((inside && focused === categories.querySelector('summary')) || focused === categoryAccess)) (categories.querySelector('a[aria-current]') || categories.querySelector('a'))?.focus({ preventScroll: true });
  };
  const sameTabClick = (event, link) => !event.defaultPrevented && event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && (!link?.target || link.target === '_self');
  const focusCategories = event => {
    categories = doc.getElementById('beverage-categories');
    if (!categories || !sameTabClick(event, categoryAccess)) return;
    categories.open = true;
    if (compact?.matches && categories.closest('[data-facets]')) {
      const opener = doc.querySelector('[data-facets-open]');
      if (opener) {
        event.preventDefault();
        opener.click();
        categories.querySelector('summary')?.focus({ preventScroll: true });
        return;
      }
    }
    view.requestAnimationFrame(() => categories.querySelector('summary')?.focus({ preventScroll: true }));
  };
  const escapeCategories = event => {
    categories = doc.getElementById('beverage-categories');
    if (event.key !== 'Escape' || !compact?.matches || !categories) return;
    const facets = categories.closest('[data-facets]');
    if (facets && facets.contains(event.target)) {
      event.preventDefault();
      event.stopPropagation();
      categories.open = false;
      facets.querySelector('[data-facets-close]')?.click();
      categoryAccess?.focus({ preventScroll: true });
    } else if (categories.open && categories.contains(event.target)) {
      event.preventDefault();
      categories.open = false;
      categories.querySelector('summary')?.focus({ preventScroll: true });
    }
  };
  resizeCategories();
  compact?.addEventListener('change', resizeCategories);
  categoryAccess?.addEventListener('click', focusCategories);
  doc.addEventListener('keydown', escapeCategories, true);
  const areaHeaderOffset = () => {
    if (!header) return 0;
    const style = view.getComputedStyle(header);
    if (!['sticky', 'fixed'].includes(style.position)) return 0;
    return Math.max(0, header.getBoundingClientRect().height + (Number.parseFloat(style.top) || 0));
  };
  const revealArea = () => {
    if (!area || view.location.hash !== '#beverage-area-menu') return;
    const obstruction = areaHeaderOffset();
    doc.documentElement.style.setProperty('--bv-header-offset', `${obstruction}px`);
    view.scrollTo({ left: view.scrollX, top: Math.max(0, area.getBoundingClientRect().top + view.scrollY - obstruction - 8), behavior: 'instant' });
    area.focus({ preventScroll: true });
  };
  const queueAreaReveal = () => {
    view.cancelAnimationFrame(areaFrame);
    areaFrame = view.requestAnimationFrame(() => {
      revealArea();
      areaFrame = view.requestAnimationFrame(revealArea);
    });
  };
  const onAreaHashChange = () => { if (view.location.hash === '#beverage-area-menu') queueAreaReveal(); };
  const focusArea = event => {
    const target = doc.getElementById('beverage-area-menu');
    if (!target || access?.hash !== '#beverage-area-menu' || !sameTabClick(event, access)) return;
    if (view.location.hash !== access.hash) catalogueReturn = { url: view.location.href, left: view.scrollX, top: view.scrollY };
    doc.documentElement.style.setProperty('--bv-header-offset', `${areaHeaderOffset()}px`);
    queueAreaReveal();
  };
  const restoreCatalogue = () => {
    if (!catalogueReturn || view.location.href !== catalogueReturn.url) return;
    const saved = catalogueReturn;
    catalogueReturn = null;
    restoreTimer = view.setTimeout(() => {
      view.scrollTo({ left: saved.left, top: saved.top, behavior: 'instant' });
      access?.focus({ preventScroll: true });
    }, 0);
  };
  const observer = view.ResizeObserver ? new view.ResizeObserver(schedule) : null;
  if (header) observer?.observe(header);
  observer?.observe(root);
  access?.addEventListener('click', focusArea);
  view.addEventListener('scroll', schedule, { passive: true });
  view.addEventListener('resize', schedule);
  view.addEventListener('popstate', restoreCatalogue);
  view.addEventListener('hashchange', onAreaHashChange);
  measure();
  onAreaHashChange();
  return () => {
    observer?.disconnect();
    access?.removeEventListener('click', focusArea);
    categoryAccess?.removeEventListener('click', focusCategories);
    doc.removeEventListener('keydown', escapeCategories, true);
    compact?.removeEventListener('change', resizeCategories);
    view.removeEventListener('scroll', schedule);
    view.removeEventListener('resize', schedule);
    view.removeEventListener('popstate', restoreCatalogue);
    view.removeEventListener('hashchange', onAreaHashChange);
    view.cancelAnimationFrame(areaFrame);
    view.clearTimeout(restoreTimer);
    view.cancelAnimationFrame(frame);
    delete root.dataset.bvCatalogueReady;
  };
}

if (!customElements.get('qortex-beverage-catalogue')) {
  customElements.define('qortex-beverage-catalogue', class extends HTMLElement {
    connectedCallback() { this.cleanup = initBeverageCatalogue(this); }
    disconnectedCallback() { this.cleanup?.(); }
  });
}
